/**
 * Feature hook: tools / UI actions (success dialog, copy link, row selection, view-copy upload, etc.).
 * Do not use Redux dispatch directly in components; use this hook for tools actions.
 */
import { useCallback } from "react";
import { useAppDispatch } from "../hooks";
import {
  onSucessDialogOkClick,
  onCopyLinkClick,
  onRowSelectionChange,
  onViewCopyUploadChange,
  onMessageBoxOkClick,
  onCheckinDocumentProgress,
  onSuccessMessageClose,
  onAttachedDocInfoChange,
  onTeamsFileSelectionChange,
  onIfsDocumentSelectionChange,
  setAccordionExpandedPanel,
  onDocClassFormatListChange,
  onCreateViewCopyChange,
  onRecentObjectSelectedDescription,
  onSearchObjIdChange,
  onWhereClauseChange,
  onAdvObjSearchModelHide,
  onAdvObjRowSelected,
  onAdvObjResultsStatusChanged,
  onAdvObjMetaDataStatusChanged,
  onDocTitleChange,
  onDocReleaseChange,
  onCreateViewCopyDisable,
  onCreateViewCopySelect,
  onSetDocumentToDeleteOneDrive,
  onAllowSelectionChange,
  onLogicalUnitListChange,
  onSearchLuNameChange,
  onSelectedKeyLuNameChange,
  onSelectedObjectConnectionChanged,
  onSearchRevisionClick,
  onSelectedRevision,
  onRefreshSearch,
  onDocumentRevisionLoadChange,
  onNewRevisionInfoChange,
  onRecentObjectConnectionChanged,
  onRecentObjectDataLoadStatusChanged,
  onRecentObjectConnectionSelect,
  onRecentObjectSelected,
  onTokenAccessFailed,
  setSpinnerModalOnCheckingDocuments,
  onCheckinPageNameChanged,
  onRefreshCheckinNew,
  onDocCheckinTypeChange,
  onObjectConnectionDataLoad,
  onCheckinProgressChange,
  onAdvObjSearchModelSelected,
  onSuccessMessageInvoke,
  onCheckinNewErrorTrigger,
  onBasicValidation,
  onCheckinValidation,
  onAdvObjCompeleted,
  onDocumentUploadFailed,
  onDocClassFormatChange,
  onFileUploadChange,
  onFileDataChange,
  onPivotSelectedKeyChange,
  onCheckInSuccess,
  onActivePageChange,
  onNewRevisionChange,
  onNewRevisionChangeWarMsg,
  onNewRevisionTextChange,
  onCheckinNewRevisionValidation,
  onNewRevisionDocInfoChange,
  onClearCheckinNewRevision,
  onCheckinNewRevisionStatusChange,
  onAdvObjConColumnsChange,
  onMruKeyNamesChange,
  onAdvObjConSearchResultChange,
  onLoginRefreshPreActionClear,
  onIFSUserLoad,
  onLoginRefreshPreActionLoad,
  onServerErrorMessageInvoke,
  onExceptionMessageInvoke,
  onErrorMessageInvoke,
  onDocClassChange,
  onDocFormatChange,
  onCheckinNewStatusChange,
  onBasicDataProgressChange,
  onPaginationLoad,
  onCloseMoreDetailsModalClick,
  onWorkingDocumentStatusChange,
  onIfsDocumentDetailsModelTrigger,
  onSearchByChange,
  onSearchTypeChange,
  onSearchValueChange,
  onSearchButtonDisable,
  onNoOfRecordsChange,
  onEmptyResults,
  onRevisionSearchLoadOn,
  onDocumentLoadChange,
  onOneDriveDataLoad,
  onReleaseLevelChange,
  onAdvanceSearchStartDateChange,
  onAdvanceSearchEndDateChange,
  onAnyDateChecked,
  onOnlyWhereUserInvolvedChecked,
  onAdvanceSearchDocClassChange,
  onAdvanceSearchFormatChange,
  toggleSideBar,
  onSelectedPageChange,
  onRefreshCheckinNewRevision,
  onResetLangauage,
  clickedOnSameNavItem,
  onCheckInToIFSClicked,
  onClearOneDriveDownloadUrl,
  onOneDriveDocumentDownloadUrlChange,
  onCheckedOutToMe,
  onIfsDocumentLoadChange,
  onFileTypeSelection,
  onOneDriveFileTypeSelection,
  onWorkingDocumentListStatusChange,
  onWorkingDocumentModelTrigger,
  onWorkingIfsDocumentChange,
  onWorkingDocumentCheckinTypeChange,
  onWorkingDocumentChange,
  onFilterVisibilityChange,
  onFileAccessStatus,
  onCheckinExistingStatusChange,
  onRefreshCheckinExisting,
} from "../slices/toolsAction";
import { requestRefreshAllDocuments } from "../slices/documents/documentSearchSlice";
import { requestRefreshWorkingDocuments } from "../slices/workingDocument/workingDocumentSlice";
import { requestRefreshTemplates } from "../slices/templates/templatesPanelSlice";

export function useTools() {
  const dispatch = useAppDispatch();

  const onSucessDialogOkClickAction = useCallback(
    (state) => dispatch(onSucessDialogOkClick(state)),
    [dispatch]
  );
  const onCopyLinkClickAction = useCallback(
    (copyLink) => dispatch(onCopyLinkClick(copyLink)),
    [dispatch]
  );
  const onRowSelectionChangeAction = useCallback(
    (rowSelection) => dispatch(onRowSelectionChange(rowSelection)),
    [dispatch]
  );
  const onViewCopyUploadChangeAction = useCallback(
    (files, count, uploadViewCopyCount, viewRequired, uploadFiles, keepDragnDrop = false) =>
      dispatch(
        onViewCopyUploadChange(
          files,
          count,
          uploadViewCopyCount,
          viewRequired,
          uploadFiles,
          keepDragnDrop
        )
      ),
    [dispatch]
  );
  const onMessageBoxOkClickAction = useCallback(
    (state) => dispatch(onMessageBoxOkClick(state)),
    [dispatch]
  );
  const onCheckinDocumentProgressAction = useCallback(
    (status) => dispatch(onCheckinDocumentProgress(status)),
    [dispatch]
  );
  const onSuccessMessageCloseAction = useCallback(
    () => dispatch(onSuccessMessageClose()),
    [dispatch]
  );
  const onAttachedDocInfoChangeAction = useCallback(
    (docInfo) => dispatch(onAttachedDocInfoChange(docInfo)),
    [dispatch]
  );
  const onTeamsFileSelectionChangeAction = useCallback(
    (rowSelection) => dispatch(onTeamsFileSelectionChange(rowSelection)),
    [dispatch]
  );
  const onIfsDocumentSelectionChangeAction = useCallback(
    (rowSelection) => dispatch(onIfsDocumentSelectionChange(rowSelection)),
    [dispatch]
  );
  const setAccordionExpandedPanelAction = useCallback(
    (panel) => dispatch(setAccordionExpandedPanel(panel)),
    [dispatch]
  );
  const onDocClassFormatListChangeAction = useCallback(
    (list) => dispatch(onDocClassFormatListChange(list)),
    [dispatch]
  );
  const onCreateViewCopyChangeAction = useCallback(
    (value) => dispatch(onCreateViewCopyChange(value)),
    [dispatch]
  );
  const onRecentObjectSelectedDescriptionAction = useCallback(
    (description) => dispatch(onRecentObjectSelectedDescription(description)),
    [dispatch]
  );
  const onSearchObjIdChangeAction = useCallback(
    (objId) => dispatch(onSearchObjIdChange(objId)),
    [dispatch]
  );
  const onWhereClauseChangeAction = useCallback(
    (para) => dispatch(onWhereClauseChange(para)),
    [dispatch]
  );
  const onAdvObjSearchModelHideAction = useCallback(
    (isHide) => dispatch(onAdvObjSearchModelHide(isHide)),
    [dispatch]
  );
  const onAdvObjRowSelectedAction = useCallback(
    (rowSelection) => dispatch(onAdvObjRowSelected(rowSelection)),
    [dispatch]
  );
  const onAdvObjResultsStatusChangedAction = useCallback(
    (status) => dispatch(onAdvObjResultsStatusChanged(status)),
    [dispatch]
  );
  const onAdvObjMetaDataStatusChangedAction = useCallback(
    (status) => dispatch(onAdvObjMetaDataStatusChanged(status)),
    [dispatch]
  );
  const onDocTitleChangeAction = useCallback(
    (title) => dispatch(onDocTitleChange(title)),
    [dispatch]
  );
  const onDocReleaseChangeAction = useCallback(
    (isChecked) => dispatch(onDocReleaseChange(isChecked)),
    [dispatch]
  );
  const onCreateViewCopyDisableAction = useCallback(
    (disable) => dispatch(onCreateViewCopyDisable(disable)),
    [dispatch]
  );
  const onCreateViewCopySelectAction = useCallback(
    (selected, options) =>
      dispatch(onCreateViewCopySelect(selected, options)),
    [dispatch]
  );
  const onSetDocumentToDeleteOneDriveAction = useCallback(
    (docInfo) => dispatch(onSetDocumentToDeleteOneDrive(docInfo)),
    [dispatch]
  );
  const onAllowSelectionChangeAction = useCallback(
    (isAllow) => dispatch(onAllowSelectionChange(isAllow)),
    [dispatch]
  );
  const onLogicalUnitListChangeAction = useCallback(
    (list) => dispatch(onLogicalUnitListChange(list)),
    [dispatch]
  );
  const onSearchLuNameChangeAction = useCallback(
    (luName, nameDesc, viewName) =>
      dispatch(onSearchLuNameChange(luName, nameDesc, viewName)),
    [dispatch],
  );
  const onSelectedKeyLuNameChangeAction = useCallback(
    (key) => dispatch(onSelectedKeyLuNameChange(key)),
    [dispatch]
  );
  const onSelectedObjectConnectionChangedAction = useCallback(
    (recentObjects, searchObjects) =>
      dispatch(onSelectedObjectConnectionChanged(recentObjects, searchObjects)),
    [dispatch]
  );
  const onSearchRevisionClickAction = useCallback(
    (isHide) => dispatch(onSearchRevisionClick(isHide)),
    [dispatch]
  );
  const onSelectedRevisionAction = useCallback(
    (selectedRows) => dispatch(onSelectedRevision(selectedRows)),
    [dispatch]
  );
  const onRefreshSearchAction = useCallback(
    () => dispatch(onRefreshSearch()),
    [dispatch]
  );
  const onDocumentRevisionLoadChangeAction = useCallback(
    (status) => dispatch(onDocumentRevisionLoadChange(status)),
    [dispatch]
  );
  const onNewRevisionInfoChangeAction = useCallback(
    (status) => dispatch(onNewRevisionInfoChange(status)),
    [dispatch]
  );
  const onRecentObjectConnectionChangedAction = useCallback(
    (objCons) => dispatch(onRecentObjectConnectionChanged(objCons)),
    [dispatch]
  );
  const onRecentObjectDataLoadStatusChangedAction = useCallback(
    (objCons) => dispatch(onRecentObjectDataLoadStatusChanged(objCons)),
    [dispatch]
  );
  const onRecentObjectConnectionSelectAction = useCallback(
    (selectedList) => dispatch(onRecentObjectConnectionSelect(selectedList)),
    [dispatch]
  );
  const onRecentObjectSelectedAction = useCallback(
    (selectedList) => dispatch(onRecentObjectSelected(selectedList)),
    [dispatch]
  );
  const onTokenAccessFailedAction = useCallback(
    (failed) => dispatch(onTokenAccessFailed(failed)),
    [dispatch]
  );
  const setSpinnerModalOnCheckingDocumentsAction = useCallback(
    (text) => dispatch(setSpinnerModalOnCheckingDocuments(text)),
    [dispatch]
  );
  const onCheckinPageNameChangedAction = useCallback(
    (name) => dispatch(onCheckinPageNameChanged(name)),
    [dispatch]
  );
  const onRefreshCheckinNewAction = useCallback(
    (completeClear) => dispatch(onRefreshCheckinNew(completeClear)),
    [dispatch]
  );
  const onDocCheckinTypeChangeAction = useCallback(
    (type) => dispatch(onDocCheckinTypeChange(type)),
    [dispatch]
  );
  const onObjectConnectionDataLoadAction = useCallback(
    (status) => dispatch(onObjectConnectionDataLoad(status)),
    [dispatch]
  );
  const onCheckinProgressChangeAction = useCallback(
    (status) => dispatch(onCheckinProgressChange(status)),
    [dispatch]
  );
  const onAdvObjSearchModelSelectedAction = useCallback(
    (isSelected) => dispatch(onAdvObjSearchModelSelected(isSelected)),
    [dispatch]
  );
  const onSuccessMessageInvokeAction = useCallback(
    (success) => dispatch(onSuccessMessageInvoke(success)),
    [dispatch]
  );
  const onCheckinNewErrorTriggerAction = useCallback(
    (message) => dispatch(onCheckinNewErrorTrigger(message)),
    [dispatch]
  );
  const onBasicValidationAction = useCallback(
    (viewCopyRequired, formatRequired, uploadViewCopyCount, createViewCopy, formatName, className) =>
      dispatch(onBasicValidation(viewCopyRequired, formatRequired, uploadViewCopyCount, createViewCopy, formatName, className)),
    [dispatch]
  );
  const onCheckinValidationAction = useCallback(
    (objConRequired, selectedObjectConnections, className) =>
      dispatch(onCheckinValidation(objConRequired, selectedObjectConnections, className)),
    [dispatch]
  );
  const onAdvObjCompeletedAction = useCallback(
    (isCompleted) => dispatch(onAdvObjCompeleted(isCompleted)),
    [dispatch]
  );
  const onDocumentUploadFailedAction = useCallback(
    (failed) => dispatch(onDocumentUploadFailed(failed)),
    [dispatch]
  );
  const onDocClassFormatChangeAction = useCallback(
    (...args) => dispatch(onDocClassFormatChange(...args)),
    [dispatch]
  );
  const onFileUploadChangeAction = useCallback(
    (...args) => dispatch(onFileUploadChange(...args)),
    [dispatch]
  );
  const onFileDataChangeAction = useCallback(
    (files, count) => dispatch(onFileDataChange(files, count)),
    [dispatch]
  );
  const onPivotSelectedKeyChangeAction = useCallback(
    (key) => dispatch(onPivotSelectedKeyChange(key)),
    [dispatch]
  );
  const onCheckInSuccessAction = useCallback(
    (checkIn) => dispatch(onCheckInSuccess(checkIn)),
    [dispatch]
  );
  const onActivePageChangeAction = useCallback(
    (currentPage) => dispatch(onActivePageChange(currentPage)),
    [dispatch]
  );
  const onNewRevisionChangeAction = useCallback(
    (newRevision) => dispatch(onNewRevisionChange(newRevision)),
    [dispatch]
  );
  const onNewRevisionChangeWarMsgAction = useCallback(
    (warMsg) => dispatch(onNewRevisionChangeWarMsg(warMsg)),
    [dispatch]
  );
  const onNewRevisionTextChangeAction = useCallback(
    (text) => dispatch(onNewRevisionTextChange(text)),
    [dispatch]
  );
  const onCheckinNewRevisionValidationAction = useCallback(
    (...args) => dispatch(onCheckinNewRevisionValidation(...args)),
    [dispatch]
  );
  const onNewRevisionDocInfoChangeAction = useCallback(
    (docInfo) => dispatch(onNewRevisionDocInfoChange(docInfo)),
    [dispatch]
  );
  const onClearCheckinNewRevisionAction = useCallback(
    () => dispatch(onClearCheckinNewRevision()),
    [dispatch]
  );
  const onCheckinNewRevisionStatusChangeAction = useCallback(
    (status) => dispatch(onCheckinNewRevisionStatusChange(status)),
    [dispatch]
  );
  const onAdvObjConColumnsChangeAction = useCallback(
    (key) => dispatch(onAdvObjConColumnsChange(key)),
    [dispatch]
  );
  const onMruKeyNamesChangeAction = useCallback(
    (key) => dispatch(onMruKeyNamesChange(key)),
    [dispatch]
  );
  const onAdvObjConSearchResultChangeAction = useCallback(
    (key) => dispatch(onAdvObjConSearchResultChange(key)),
    [dispatch]
  );
  const onLoginRefreshPreActionClearAction = useCallback(
    () => dispatch(onLoginRefreshPreActionClear()),
    [dispatch]
  );
  const onIFSUserLoadAction = useCallback(
    (status) => dispatch(onIFSUserLoad(status)),
    [dispatch]
  );
  const onLoginRefreshPreActionLoadAction = useCallback(
    (preAction) => dispatch(onLoginRefreshPreActionLoad(preAction)),
    [dispatch]
  );
  const onServerErrorMessageInvokeAction = useCallback(
    (status) => dispatch(onServerErrorMessageInvoke(status)),
    [dispatch]
  );
  const onExceptionMessageInvokeAction = useCallback(
    (exception) => dispatch(onExceptionMessageInvoke(exception)),
    [dispatch]
  );
  const onErrorMessageInvokeAction = useCallback(
    (message) => dispatch(onErrorMessageInvoke(message)),
    [dispatch]
  );
  const onDocClassChangeAction = useCallback(
    (classCode) => dispatch(onDocClassChange(classCode)),
    [dispatch]
  );
  const onDocFormatChangeAction = useCallback(
    (formatCode) => dispatch(onDocFormatChange(formatCode)),
    [dispatch]
  );
  const onCheckinNewStatusChangeAction = useCallback(
    (status) => dispatch(onCheckinNewStatusChange(status)),
    [dispatch]
  );
  const onBasicDataProgressChangeAction = useCallback(
    (status) => dispatch(onBasicDataProgressChange(status)),
    [dispatch]
  );
  const onPaginationLoadAction = useCallback(
    (status) => dispatch(onPaginationLoad(status)),
    [dispatch]
  );
  const onCloseMoreDetailsModalClickAction = useCallback(
    (state) => dispatch(onCloseMoreDetailsModalClick(state)),
    [dispatch]
  );
  const onWorkingDocumentStatusChangeAction = useCallback(
    (status) => dispatch(onWorkingDocumentStatusChange(status)),
    [dispatch]
  );
  const onIfsDocumentDetailsModelTriggerAction = useCallback(
    (open) => dispatch(onIfsDocumentDetailsModelTrigger(open)),
    [dispatch]
  );
  const onSearchByChangeAction = useCallback(
    (searchBy) => dispatch(onSearchByChange(searchBy)),
    [dispatch]
  );
  const onSearchTypeChangeAction = useCallback(
    (searchType) => dispatch(onSearchTypeChange(searchType)),
    [dispatch]
  );
  const onSearchValueChangeAction = useCallback(
    (searchValue) => dispatch(onSearchValueChange(searchValue)),
    [dispatch]
  );
  const onSearchButtonDisableAction = useCallback(
    (isDiable) => dispatch(onSearchButtonDisable(isDiable)),
    [dispatch]
  );
  const onNoOfRecordsChangeAction = useCallback(
    (noOfRecords) => dispatch(onNoOfRecordsChange(noOfRecords)),
    [dispatch]
  );
  const onEmptyResultsAction = useCallback(
    (message) => dispatch(onEmptyResults(message)),
    [dispatch]
  );
  const onRevisionSearchLoadOnAction = useCallback(
    (isLoad) => dispatch(onRevisionSearchLoadOn(isLoad)),
    [dispatch]
  );
  const onDocumentLoadChangeAction = useCallback(
    (status) => dispatch(onDocumentLoadChange(status)),
    [dispatch]
  );
  const onOneDriveDataLoadAction = useCallback(
    (status) => dispatch(onOneDriveDataLoad(status)),
    [dispatch]
  );
  const onReleaseLevelChangeAction = useCallback(
    (releaseLevel) => dispatch(onReleaseLevelChange(releaseLevel)),
    [dispatch]
  );
  const onAdvanceSearchStartDateChangeAction = useCallback(
    (startDate, endDate) => dispatch(onAdvanceSearchStartDateChange(startDate, endDate)),
    [dispatch]
  );
  const onAdvanceSearchEndDateChangeAction = useCallback(
    (endDate) => dispatch(onAdvanceSearchEndDateChange(endDate)),
    [dispatch]
  );
  const onAnyDateCheckedAction = useCallback(
    (state) => dispatch(onAnyDateChecked(state)),
    [dispatch]
  );
  const onOnlyWhereUserInvolvedCheckedAction = useCallback(
    (onlyUserInvoved) => dispatch(onOnlyWhereUserInvolvedChecked(onlyUserInvoved)),
    [dispatch]
  );
  const onAdvanceSearchDocClassChangeAction = useCallback(
    (docClass, getDocClassFormats) => dispatch(onAdvanceSearchDocClassChange(docClass, getDocClassFormats)),
    [dispatch]
  );
  const onAdvanceSearchFormatChangeAction = useCallback(
    (format) => dispatch(onAdvanceSearchFormatChange(format)),
    [dispatch]
  );
  const toggleSideBarAction = useCallback(
    (toggle) => dispatch(toggleSideBar(toggle)),
    [dispatch]
  );
  const onSelectedPageChangeAction = useCallback(
    (pageName) => dispatch(onSelectedPageChange(pageName)),
    [dispatch]
  );
  const onRefreshCheckinNewRevisionAction = useCallback(
    (completeClear) => dispatch(onRefreshCheckinNewRevision(completeClear)),
    [dispatch]
  );
  const onResetLangauageAction = useCallback(
    (pageLoadLanguage) => dispatch(onResetLangauage(pageLoadLanguage)),
    [dispatch]
  );
  const clickedOnSameNavItemAction = useCallback(
    (isClicked) => dispatch(clickedOnSameNavItem(isClicked)),
    [dispatch]
  );
  const onCheckInToIFSClickedAction = useCallback(
    (isCheckInToIFSClicked) => dispatch(onCheckInToIFSClicked(isCheckInToIFSClicked)),
    [dispatch]
  );
  const onClearOneDriveDownloadUrlAction = useCallback(
    (shouldClearUrl) => dispatch(onClearOneDriveDownloadUrl(shouldClearUrl)),
    [dispatch]
  );
  const onOneDriveDocumentDownloadUrlChangeAction = useCallback(
    (url) => dispatch(onOneDriveDocumentDownloadUrlChange(url)),
    [dispatch]
  );
  const onCheckedOutToMeAction = useCallback(
    (checkedOutToMe) => dispatch(onCheckedOutToMe(checkedOutToMe)),
    [dispatch]
  );
  const onIfsDocumentLoadChangeAction = useCallback(
    (status) => dispatch(onIfsDocumentLoadChange(status)),
    [dispatch]
  );
  const onFileTypeSelectionAction = useCallback(
    (fileType) => dispatch(onFileTypeSelection(fileType)),
    [dispatch]
  );
  const onOneDriveFileTypeSelectionAction = useCallback(
    (fileType) => dispatch(onOneDriveFileTypeSelection(fileType)),
    [dispatch]
  );
  const onWorkingDocumentListStatusChangeAction = useCallback(
    (status) => dispatch(onWorkingDocumentListStatusChange(status)),
    [dispatch]
  );
  const onWorkingDocumentModelTriggerAction = useCallback(
    (open) => dispatch(onWorkingDocumentModelTrigger(open)),
    [dispatch]
  );
  const onWorkingIfsDocumentChangeAction = useCallback(
    (document) => dispatch(onWorkingIfsDocumentChange(document)),
    [dispatch]
  );
  const onWorkingDocumentCheckinTypeChangeAction = useCallback(
    (status) => dispatch(onWorkingDocumentCheckinTypeChange(status)),
    [dispatch]
  );
  const onWorkingDocumentChangeAction = useCallback(
    (document) => dispatch(onWorkingDocumentChange(document)),
    [dispatch]
  );
  const onFilterVisibilityChangeAction = useCallback(
    (isVisible) => dispatch(onFilterVisibilityChange(isVisible)),
    [dispatch]
  );
  const onFileAccessStatusAction = useCallback(
    (fileType) => dispatch(onFileAccessStatus(fileType)),
    [dispatch]
  );
  const onCheckinExistingStatusChangeAction = useCallback(
    (status) => dispatch(onCheckinExistingStatusChange(status)),
    [dispatch]
  );
  const onRefreshCheckinExistingAction = useCallback(
    () => dispatch(onRefreshCheckinExisting()),
    [dispatch]
  );
  const onRequestRefreshAllDocumentsAction = useCallback(
    () => dispatch(requestRefreshAllDocuments()),
    [dispatch]
  );
  const onRequestRefreshWorkingDocumentsAction = useCallback(
    () => dispatch(requestRefreshWorkingDocuments()),
    [dispatch]
  );
  const onRequestRefreshTemplatesAction = useCallback(
    () => dispatch(requestRefreshTemplates()),
    [dispatch]
  );

  return {
    onSucessDialogOkClick: onSucessDialogOkClickAction,
    onCopyLinkClick: onCopyLinkClickAction,
    onRowSelectionChange: onRowSelectionChangeAction,
    onViewCopyUploadChange: onViewCopyUploadChangeAction,
    onMessageBoxOkClick: onMessageBoxOkClickAction,
    onCheckinDocumentProgress: onCheckinDocumentProgressAction,
    onSuccessMessageClose: onSuccessMessageCloseAction,
    onAttachedDocInfoChange: onAttachedDocInfoChangeAction,
    onTeamsFileSelectionChange: onTeamsFileSelectionChangeAction,
    onIfsDocumentSelectionChange: onIfsDocumentSelectionChangeAction,
    setAccordionExpandedPanel: setAccordionExpandedPanelAction,
    onDocClassFormatListChange: onDocClassFormatListChangeAction,
    onCreateViewCopyChange: onCreateViewCopyChangeAction,
    onRecentObjectSelectedDescription: onRecentObjectSelectedDescriptionAction,
    onSearchObjIdChange: onSearchObjIdChangeAction,
    onWhereClauseChange: onWhereClauseChangeAction,
    onAdvObjSearchModelHide: onAdvObjSearchModelHideAction,
    onAdvObjRowSelected: onAdvObjRowSelectedAction,
    onAdvObjResultsStatusChanged: onAdvObjResultsStatusChangedAction,
    onAdvObjMetaDataStatusChanged: onAdvObjMetaDataStatusChangedAction,
    onDocTitleChange: onDocTitleChangeAction,
    onDocReleaseChange: onDocReleaseChangeAction,
    onCreateViewCopyDisable: onCreateViewCopyDisableAction,
    onCreateViewCopySelect: onCreateViewCopySelectAction,
    onSetDocumentToDeleteOneDrive: onSetDocumentToDeleteOneDriveAction,
    onAllowSelectionChange: onAllowSelectionChangeAction,
    onLogicalUnitListChange: onLogicalUnitListChangeAction,
    onSearchLuNameChange: onSearchLuNameChangeAction,
    onSelectedKeyLuNameChange: onSelectedKeyLuNameChangeAction,
    onSelectedObjectConnectionChanged: onSelectedObjectConnectionChangedAction,
    onSearchRevisionClick: onSearchRevisionClickAction,
    onSelectedRevision: onSelectedRevisionAction,
    onRefreshSearch: onRefreshSearchAction,
    onDocumentRevisionLoadChange: onDocumentRevisionLoadChangeAction,
    onNewRevisionInfoChange: onNewRevisionInfoChangeAction,
    onRecentObjectConnectionChanged: onRecentObjectConnectionChangedAction,
    onRecentObjectDataLoadStatusChanged: onRecentObjectDataLoadStatusChangedAction,
    onRecentObjectConnectionSelect: onRecentObjectConnectionSelectAction,
    onRecentObjectSelected: onRecentObjectSelectedAction,
    onTokenAccessFailed: onTokenAccessFailedAction,
    setSpinnerModalOnCheckingDocuments: setSpinnerModalOnCheckingDocumentsAction,
    onCheckinPageNameChanged: onCheckinPageNameChangedAction,
    onRefreshCheckinNew: onRefreshCheckinNewAction,
    onDocCheckinTypeChange: onDocCheckinTypeChangeAction,
    onObjectConnectionDataLoad: onObjectConnectionDataLoadAction,
    onCheckinProgressChange: onCheckinProgressChangeAction,
    onAdvObjSearchModelSelected: onAdvObjSearchModelSelectedAction,
    onSuccessMessageInvoke: onSuccessMessageInvokeAction,
    onCheckinNewErrorTrigger: onCheckinNewErrorTriggerAction,
    onBasicValidation: onBasicValidationAction,
    onCheckinValidation: onCheckinValidationAction,
    onAdvObjCompeleted: onAdvObjCompeletedAction,
    onDocumentUploadFailed: onDocumentUploadFailedAction,
    onDocClassFormatChange: onDocClassFormatChangeAction,
    onFileUploadChange: onFileUploadChangeAction,
    onFileDataChange: onFileDataChangeAction,
    onPivotSelectedKeyChange: onPivotSelectedKeyChangeAction,
    onCheckInSuccess: onCheckInSuccessAction,
    onActivePageChange: onActivePageChangeAction,
    onNewRevisionChange: onNewRevisionChangeAction,
    onNewRevisionChangeWarMsg: onNewRevisionChangeWarMsgAction,
    onNewRevisionTextChange: onNewRevisionTextChangeAction,
    onCheckinNewRevisionValidation: onCheckinNewRevisionValidationAction,
    onNewRevisionDocInfoChange: onNewRevisionDocInfoChangeAction,
    onClearCheckinNewRevision: onClearCheckinNewRevisionAction,
    onCheckinNewRevisionStatusChange: onCheckinNewRevisionStatusChangeAction,
    onAdvObjConColumnsChange: onAdvObjConColumnsChangeAction,
    onMruKeyNamesChange: onMruKeyNamesChangeAction,
    onAdvObjConSearchResultChange: onAdvObjConSearchResultChangeAction,
    onLoginRefreshPreActionClear: onLoginRefreshPreActionClearAction,
    onIFSUserLoad: onIFSUserLoadAction,
    onLoginRefreshPreActionLoad: onLoginRefreshPreActionLoadAction,
    onServerErrorMessageInvoke: onServerErrorMessageInvokeAction,
    onExceptionMessageInvoke: onExceptionMessageInvokeAction,
    onErrorMessageInvoke: onErrorMessageInvokeAction,
    onDocClassChange: onDocClassChangeAction,
    onDocFormatChange: onDocFormatChangeAction,
    onCheckinNewStatusChange: onCheckinNewStatusChangeAction,
    onBasicDataProgressChange: onBasicDataProgressChangeAction,
    onPaginationLoad: onPaginationLoadAction,
    onCloseMoreDetailsModalClick: onCloseMoreDetailsModalClickAction,
    onWorkingDocumentStatusChange: onWorkingDocumentStatusChangeAction,
    onIfsDocumentDetailsModelTrigger: onIfsDocumentDetailsModelTriggerAction,
    onSearchByChange: onSearchByChangeAction,
    onSearchTypeChange: onSearchTypeChangeAction,
    onSearchValueChange: onSearchValueChangeAction,
    onSearchButtonDisable: onSearchButtonDisableAction,
    onNoOfRecordsChange: onNoOfRecordsChangeAction,
    onEmptyResults: onEmptyResultsAction,
    onRevisionSearchLoadOn: onRevisionSearchLoadOnAction,
    onDocumentLoadChange: onDocumentLoadChangeAction,
    onOneDriveDataLoad: onOneDriveDataLoadAction,
    onReleaseLevelChange: onReleaseLevelChangeAction,
    onAdvanceSearchStartDateChange: onAdvanceSearchStartDateChangeAction,
    onAdvanceSearchEndDateChange: onAdvanceSearchEndDateChangeAction,
    onAnyDateChecked: onAnyDateCheckedAction,
    onOnlyWhereUserInvolvedChecked: onOnlyWhereUserInvolvedCheckedAction,
    onAdvanceSearchDocClassChange: onAdvanceSearchDocClassChangeAction,
    onAdvanceSearchFormatChange: onAdvanceSearchFormatChangeAction,
    toggleSideBar: toggleSideBarAction,
    onSelectedPageChange: onSelectedPageChangeAction,
    onRefreshCheckinNewRevision: onRefreshCheckinNewRevisionAction,
    onResetLangauage: onResetLangauageAction,
    clickedOnSameNavItem: clickedOnSameNavItemAction,
    onCheckInToIFSClicked: onCheckInToIFSClickedAction,
    onClearOneDriveDownloadUrl: onClearOneDriveDownloadUrlAction,
    onOneDriveDocumentDownloadUrlChange: onOneDriveDocumentDownloadUrlChangeAction,
    onCheckedOutToMe: onCheckedOutToMeAction,
    onIfsDocumentLoadChange: onIfsDocumentLoadChangeAction,
    onFileTypeSelection: onFileTypeSelectionAction,
    onOneDriveFileTypeSelection: onOneDriveFileTypeSelectionAction,
    onWorkingDocumentListStatusChange: onWorkingDocumentListStatusChangeAction,
    onWorkingDocumentModelTrigger: onWorkingDocumentModelTriggerAction,
    onWorkingIfsDocumentChange: onWorkingIfsDocumentChangeAction,
    onWorkingDocumentCheckinTypeChange: onWorkingDocumentCheckinTypeChangeAction,
    onWorkingDocumentChange: onWorkingDocumentChangeAction,
    onFilterVisibilityChange: onFilterVisibilityChangeAction,
    onFileAccessStatus: onFileAccessStatusAction,
    onCheckinExistingStatusChange: onCheckinExistingStatusChangeAction,
    onRefreshCheckinExisting: onRefreshCheckinExistingAction,
    onRequestRefreshAllDocuments: onRequestRefreshAllDocumentsAction,
    onRequestRefreshWorkingDocuments: onRequestRefreshWorkingDocumentsAction,
    onRequestRefreshTemplates: onRequestRefreshTemplatesAction,
  };
}
